import * as TokenSale from '../contracts/master'
import { Tiers } from '../types/reducerTypes'
import { bsToString } from '../utils/amount'
import Big, { BigSource } from 'big.js'
import { ethers } from 'ethers'

export interface IStakes {
  amount: Big // amount bought by user
  share: Big // to get refund = (amount - share)
  claimed: boolean
}

export enum Epoch {
  Incoming,
  Private,
  Waiting,
  Public,
  Finished,
}

export interface IMasterContract {
  totalTokenSold: () => Promise<string>
  approve: (spender: string, amount: BigSource) => Promise<void>
  deposit: (value: BigSource, address: string) => Promise<void>
  claim: (address: string) => Promise<void>
  getClaimEventsForUser: (address: string) => Promise<void>
  claimOld: (address: string, abi: ethers.InterfaceAbi) => Promise<void>
  claimCheck: (address: string) => Promise<void>
  stakes: (address: string) => Promise<IStakes>
  stakesOld: (address: string, abi: ethers.InterfaceAbi) => Promise<IStakes>
  maxValuesByTier: (address: string, tier: Tiers) => Promise<Big>
  publicPurchased: (address: string) => Promise<Big>
  epoch: (address: string) => Promise<Epoch>
  saleTokensAmountWithoutAirdrop: (address: string) => Promise<Big>
  publicMaxValues: (address: string) => Promise<Big>
  publicPrice: (address: string) => Promise<Big>
  privatePrice: (address: string) => Promise<Big>
  getState: (
    address: string,
  ) => Promise<{ totalSupply: string; totalSold: string }>
}

export const MAX_ALLOWANCE = new Big(Math.pow(2, 127))

export const isWithinAllowance = (allowance: string, toSpend: string) =>
  new Big(allowance).toNumber() > new Big(toSpend).toNumber()

const initToken = (provider: ethers.BrowserProvider, account: string) => {
  if (!provider) return
  return new ethers.Contract(
    TokenSale.address,
    TokenSale.abi as ethers.InterfaceAbi,
    provider,
  )
}

const useMasterContract = (
  provider: ethers.BrowserProvider,
  account: string,
): IMasterContract => {
  const contract = initToken(provider, account)

  async function getClaimEventsForUser(address: string) {
    // const events = await contract?.getPastEvents('Claim', {
    //   filter: { user: address },
    //   fromBlock: 0,
    //   toBlock: 'latest',
    // });
    // // return events;
  }

  async function approve(spender: string, amount: BigSource) {
    const gas = await contract?.approve.estimateGas(spender, amount)
    await contract?.approve(spender, MAX_ALLOWANCE, { gasLimit: gas })
  }

  async function totalTokenSold() {
    return await contract?.totalTokenSold()
  }

  async function deposit(value: BigSource, address: string) {
    const deposit = new ethers.Contract(
      address,
      TokenSale.abi as ethers.InterfaceAbi,
      provider,
    )
    const gas = await deposit?.deposit.estimateGas(bsToString(value))
    await deposit?.deposit(bsToString(value), { gasLimit: gas })
  }

  async function claim(address: string) {
    const claim = new ethers.Contract(
      address,
      TokenSale.abi as ethers.InterfaceAbi,
      provider,
    )
    const gas = await claim?.claim.estimateGas()
    await claim?.claim({ gasLimit: gas })
  }

  async function claimOld(address: string, abi: ethers.InterfaceAbi) {
    const claim = new ethers.Contract(
      address,
      abi as ethers.InterfaceAbi,
      provider,
    )
    const gas = await claim?.claim.estimateGas()
    await claim?.claim({ gasLimit: gas })
  }

  async function claimCheck(address: string) {
    return await contract?.stakes(address)
  }

  async function stakes(address: string): Promise<IStakes> {
    const stakes = new ethers.Contract(
      address,
      TokenSale.abi as ethers.InterfaceAbi,
      provider,
    )

    const res: any = await stakes?.stakes(account)

    return {
      amount: new Big(res.amount),
      share: new Big(res.share),
      claimed: res.claimed,
    }
  }

  async function stakesOld(
    address: string,
    abi: ethers.InterfaceAbi,
  ): Promise<IStakes> {
    const stakes = new ethers.Contract(
      address,
      abi as ethers.InterfaceAbi,
      provider,
    )
    const res: IStakes = await stakes?.stakes(account)

    return {
      amount: new Big(res.amount),
      share: new Big(res.share),
      claimed: res.claimed,
    }
  }

  async function maxValuesByTier(address: string, tier: Tiers): Promise<Big> {
    const max = new ethers.Contract(
      address,
      TokenSale.abi as ethers.InterfaceAbi,
      provider,
    )
    return new Big(await max?.maxValuesByTier(tier))
  }

  async function privatePrice(address: string): Promise<Big> {
    return new Big(0)
  }

  async function publicPrice(address: string): Promise<Big> {
    const priceContract = new ethers.Contract(
      address,
      TokenSale.abi as ethers.InterfaceAbi,
      provider,
    )
    const publicPrice = await priceContract?.publicPrice()
    return new Big(publicPrice)
  }

  async function publicPurchased(address: string): Promise<Big> {
    const publicPurchased = new ethers.Contract(
      address,
      TokenSale.abi as ethers.InterfaceAbi,
      provider,
    )
    return new Big(await publicPurchased?.publicPurchased(account))
  }

  async function epoch(address: string): Promise<Epoch> {
    const epoch = new ethers.Contract(
      address,
      TokenSale.abi as ethers.InterfaceAbi,
      provider,
    )
    return await epoch?.epoch()
  }

  async function saleTokensAmountWithoutAirdrop(address: string): Promise<Big> {
    const supply = new ethers.Contract(
      address,
      TokenSale.abi as ethers.InterfaceAbi,
      provider,
    )
    return new Big(await supply?.saleTokensAmountWithoutAirdrop())
  }

  async function publicMaxValues(address: string): Promise<Big> {
    const maxValues = new ethers.Contract(
      address,
      TokenSale.abi as ethers.InterfaceAbi,
      provider,
    )
    return new Big(await maxValues?.publicMaxValues())
  }

  async function getGiftedTier(address: string): Promise<string> {
    const giftedTierLevel = address ? await contract?.giftedTier(address) : 0
    return giftedTierLevel
  }

  async function getState(
    address: string,
  ): Promise<{ totalSupply: string; totalSold: string }> {
    const [totalSold, totalSupply] = address ? await contract?.getState() : 0
    return { totalSupply, totalSold }
  }

  return {
    approve,
    deposit,
    claim,
    claimOld,
    claimCheck,
    stakes,
    stakesOld,
    maxValuesByTier,
    publicPurchased,
    epoch,
    saleTokensAmountWithoutAirdrop,
    publicMaxValues,
    privatePrice,
    publicPrice,
    totalTokenSold,
    getClaimEventsForUser,
    getState,
  }
}

export default useMasterContract
